// Types + fetch for the admin Run screen (#262, ADR 0064) — absorbed from
// load-generator-ui's "Run" tab. Unlike that tab, this calls the BFF
// (`/api/admin/run`, cookie auth) rather than the load-generator API
// directly with an `x-load-generator-secret` header — the browser never
// holds that secret now, the BFF does. Contract otherwise unchanged:
// services/load-generator/openapi.yaml is still the source of truth for the
// request/response shapes.

export const MAX_COUNT = 10000;
export const MAX_DURATION_SECONDS = 3600;

export const FAILURE_SERVICES = [
  "payment-service",
  "inventory-service",
  "shipping-service",
  "fraud-check-service",
  "risk-service",
] as const;

export const FAILURE_MODES = ["exception", "timeout", "partial"] as const;

export type FailureService = (typeof FAILURE_SERVICES)[number];
export type FailureMode = (typeof FAILURE_MODES)[number];

export interface OrderItem {
  sku: string;
  quantity: number;
  unitPrice: number;
}

export interface OrderOverride {
  currency?: string;
  customerId?: string;
  items?: OrderItem[];
}

export interface ForceFailure {
  service: FailureService;
  mode: FailureMode;
}

export interface LoadRequest {
  count?: number;
  durationSeconds?: number;
  ratePerMinute?: number;
  order?: OrderOverride;
  forceFailure?: ForceFailure;
}

export interface RunResult {
  requested: number;
  dispatched: number;
  failed?: number;
  orderIds: string[];
  errors?: string[];
  startedAt: string;
  finishedAt: string;
}

export interface AsyncHandoff {
  accepted: number;
  durationSeconds: number;
  mode: "burst" | "spread";
  async: true;
}

export interface ApiError {
  error: string;
}

export type RunResponse =
  | { kind: "result"; status: 200; body: RunResult }
  | { kind: "async"; status: 202; body: AsyncHandoff }
  // No session — the caller should send the visitor to sign in, same
  // convention as stock.ts / catalog.ts.
  | { kind: "unauthorized" }
  | { kind: "error"; status: number; body: ApiError };

export async function generateLoad(req: LoadRequest): Promise<RunResponse> {
  const res = await fetch("/api/admin/run", {
    method: "POST",
    credentials: "include",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(req),
  });

  if (res.status === 401) {
    return { kind: "unauthorized" };
  }

  let body: unknown;
  try {
    body = await res.json();
  } catch {
    body = { error: `${res.status} ${res.statusText} (non-JSON response)` };
  }

  if (res.status === 200) {
    return { kind: "result", status: 200, body: body as RunResult };
  }
  if (res.status === 202) {
    return { kind: "async", status: 202, body: body as AsyncHandoff };
  }
  return { kind: "error", status: res.status, body: body as ApiError };
}
